import { AuthContextProps } from 'react-oidc-context';

/**
 * Handles user logout with cleanup and error handling
 * @param auth - The auth context from react-oidc-context
 * @param options - Additional options for logout
 */
export const handleLogout = async (
  auth: AuthContextProps,
  options?: {
    clearLocalStorage?: boolean;
    redirectUrl?: string;
  }
) => {
  try {
    // Mark that this is an intentional logout
    localStorage.setItem('intentional_logout', 'true');
    
    // Clear any application-specific data if needed
    if (options?.clearLocalStorage) {
      // Add any specific keys you want to clear here if needed
      // localStorage.removeItem('your-app-specific-key');
    }

    console.log('Attempting logout...');


    // Clear the user session locally to remove cached credentials
    await auth.removeUser();

    // The correct way to do this is by calling signoutRedirect(). BUT, Cognito doesn't
    // support the "post_logout_redirect_uri" query parameter which it adds to the /logout URL.
    // Cognito expects "logout_uri" instead. So, we need to build the /logout URI ourselves.
    // https://docs.aws.amazon.com/cognito/latest/developerguide/logout-endpoint.html
    //auth.signoutRedirect();
    window.location.href =
      process.env.REACT_APP_END_SESSION_ENDPOINT +
      `?logout_uri=${encodeURIComponent(window.location.origin)}` +
      `&client_id=${encodeURIComponent(process.env.REACT_APP_OIDC_CLIENT_ID!)}`;
  } catch (error) {
    console.error('Logout failed:', error);
  }
};

/**
 * Decodes a JWT token and returns the payload
 * @param token - The JWT token to decode
 * @returns The decoded payload or null if invalid
 */
export function decodeJWT(token: string): any {
  try {
    // JWT has three parts separated by dots: header.payload.signature
    const parts = token.split('.');
    if (parts.length !== 3) {
      console.error('Invalid JWT token format');
      return null;
    }

    // Decode the payload (second part)
    const payload = parts[1];
    
    // Add padding if needed for base64 decoding
    const paddedPayload = payload + '==='.slice((payload.length + 3) % 4);
    
    // Decode from base64
    const decodedPayload = atob(paddedPayload);
    
    // Parse as JSON
    return JSON.parse(decodedPayload);
  } catch (error) {
    console.error('Error decoding JWT token:', error);
    return null;
  }
}

/**
 * Extracts cognito groups from a JWT token
 * @param token - The JWT access token
 * @returns Array of cognito groups or empty array if none found
 */
export function getCognitoGroups(token: string): string[] {
  const payload = decodeJWT(token);
  
  if (!payload) {
    return [];
  }

  // Extract cognito:groups claim
  const cognitoGroups = payload['cognito:groups'];
  
  if (Array.isArray(cognitoGroups)) {
    return cognitoGroups;
  }
  
  return [];
}

export function getTenantIdFromCognitoGroups(groups: string[]): string | undefined {
  const tenantGroup = groups.find((group) => group.startsWith('tenant_'));
  return tenantGroup?.slice('tenant_'.length);
}

export function getTenantIdFromAccessToken(token?: string): string | undefined {
  return token ? getTenantIdFromCognitoGroups(getCognitoGroups(token)) : undefined;
}

export function isIvoLicensesTenant(tenantId?: string): boolean {
  return tenantId === 'breadify' || tenantId === 'stockholmstad' || tenantId === 'larmtjanst';
}

export function isCarEntityListTenant(tenantId?: string): boolean {
  return tenantId === 'fordonskonsult';
}

export function isTenantCaseTenant(tenantId?: string): boolean {
  return tenantId === 'fordonskonsult';
}
